import { useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { toast } from 'sonner'
import { cnpjMask, onlyDigits } from '@/components/Deals/DealDetailPanel/utils'
import { cadastroPatchFromCnpjLookup } from '@/components/ClientesAgrada/cadastroCnpjLookup'
import type { CadastroClienteForm } from '@/components/ClientesAgrada/ClientesAgradaCadastroFields'
import api from '@/services/api'
import { getFriendlyError } from '@/utils/getFriendlyError'

const fieldClass =
  'w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-sm text-text-primary'

type Props = {
  value: string
  onChange: (patch: Partial<CadastroClienteForm>) => void
}

export function CadastroCnpjField({ value, onChange }: Props) {
  const [searching, setSearching] = useState(false)
  const lastLookup = useRef('')

  const lookup = async (digits: string) => {
    if (digits.length !== 14 || digits === lastLookup.current || searching) return
    lastLookup.current = digits
    setSearching(true)
    try {
      const { data } = await api.get(`/empresas/cnpj/${digits}?source=brasilapi`)
      const empresa = data.data ?? data
      if (!empresa) {
        toast.error('CNPJ não encontrado')
        lastLookup.current = ''
        return
      }
      onChange({
        cnpj: cnpjMask(digits),
        ...cadastroPatchFromCnpjLookup(empresa, digits),
      })
      toast.success('Dados da empresa preenchidos')
    } catch (err) {
      lastLookup.current = ''
      toast.error(getFriendlyError(err, 'Erro ao buscar CNPJ'))
    } finally {
      setSearching(false)
    }
  }

  return (
    <div className='flex gap-2'>
      <input
        className={`${fieldClass} font-mono tracking-wider`}
        required
        inputMode='numeric'
        placeholder='00.000.000/0000-00'
        value={value}
        onChange={(event) => {
          const masked = cnpjMask(event.target.value)
          onChange({ cnpj: masked })
          const digits = onlyDigits(masked)
          if (digits.length === 14) void lookup(digits)
        }}
      />
      <button
        type='button'
        className='inline-flex shrink-0 items-center gap-1 rounded-lg border border-[var(--glass-border)] px-3 text-xs text-text-primary disabled:opacity-60'
        disabled={searching}
        onClick={() => {
          const digits = onlyDigits(value)
          if (digits.length !== 14) {
            toast.error('CNPJ inválido — precisa ter 14 dígitos')
            return
          }
          lastLookup.current = ''
          void lookup(digits)
        }}
      >
        <Search size={13} />
        {searching ? 'Buscando…' : 'Buscar'}
      </button>
    </div>
  )
}
